/**
 * analytics.js — Anonymous browse-funnel event tracking for the Guild.
 *
 * Two sinks per call:
 *   1) GA4 via window.gtag (no-op when gtag isn't loaded).
 *   2) Server-side `recordAnalyticsEvent` Firestore log via Cloud Function.
 *
 * Privacy:
 * - Honors `localStorage.consent.analytics` (set by ConsentBanner). When
 *   consent is missing or false, this util does nothing.
 * - No PII transmitted. `sessionId` is a UUID minted in localStorage; the
 *   server hashes IP+UA into one opaque fingerprint, never storing user id.
 *
 * Usage:
 *   import { trackEvent } from "../utils/analytics";
 *   trackEvent("listing_view", { refId: listingId, refType: "business" });
 */
import { httpsCallable } from "firebase/functions";
import { functions } from "../FirebaseConfig";

const SESSION_KEY = "guild.analytics.sessionId";
const CONSENT_KEY = "guild.consent";

function safeUUID() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (_e) {
    /* ignore */
  }
  // Fallback — sufficient for an opaque session id, not cryptographic.
  return "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getSessionId() {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = safeUUID();
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch (_e) {
    return "no-storage";
  }
}

export function hasAnalyticsConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed && parsed.analytics === true;
  } catch (_e) {
    return false;
  }
}

let cachedCallable = null;
function getCallable() {
  if (cachedCallable) return cachedCallable;
  try {
    cachedCallable = httpsCallable(functions, "recordAnalyticsEvent");
  } catch (_e) {
    cachedCallable = null;
  }
  return cachedCallable;
}

function fireGtag(type, params) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", type, params || {});
    }
  } catch (_e) {
    /* ignore */
  }
}

/**
 * Fire an analytics event. Always safe to call — silent if consent is denied
 * or the callable isn't reachable. Never throws.
 *
 * @param {string} type   One of the ALLOWED_TYPES in functions/analyticsFn.js.
 * @param {object} [opts]
 *   @param {string} [opts.refId]   ID of the referenced object (listing, event).
 *   @param {string} [opts.refType] Kind of the referenced object.
 *   @param {object} [opts.meta]    Small extra payload (<=1KB serialized).
 */
export function trackEvent(type, opts = {}) {
  if (!type) return;
  if (!hasAnalyticsConsent()) return;

  const { refId, refType, meta } = opts;
  const sessionId = getSessionId();

  fireGtag(type, {
    ref_id: refId || undefined,
    ref_type: refType || undefined,
    session_id: sessionId,
    ...(meta || {}),
  });

  const call = getCallable();
  if (!call) return;
  call({ type, sessionId, refId, refType, meta }).catch(() => {
    // Analytics must never break UX.
  });
}

export default trackEvent;
